import { useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Plus, X } from "lucide-react"
import { Section, Field, ToggleField } from "../../shared/components/EditorFields"
import { ImageField } from "../../shared/components/ImageField"
import { ServiceMap, CoordinateReadout } from "../ServiceMap"
import { EntityPicker, MultiPicker, RowList } from "./pickers"
import {
  type ServiceStore, type EntityKind, type HourDay,
  HOUR_DAYS, CONTACT_CHANNELS, SERVICE_STATUSES, serviceStatusFor,
} from "../types"
import * as db from "../store"

export function ServiceFields({
  store, locale, editLocale, service, set, setT, onSelect,
}: {
  store: ServiceStore
  locale: string
  /** The locale whose translated fields are being edited. */
  editLocale: string
  service: any
  set: (patch: Record<string, any>) => void
  setT: (patch: Record<string, any>) => void
  onSelect: (kind: EntityKind, id: string) => void
}) {
  const tr = service.translations?.[editLocale]
  const hasOwnStatus = !!tr?.status
  const localeStatus = serviceStatusFor(service, editLocale)

  // Writing to the default locale sets the service-wide status, so locales that
  // have not been set explicitly keep following it.
  //
  // For any OTHER locale the status must land on that locale's own translation
  // row — including when no row exists yet. Falling back to the service-wide
  // status here (as this used to) silently republished every locale that was
  // still following the default, from a control labelled with one locale.
  const setStatusForLocale = (next: string) => {
    if (editLocale === store.defaultLocale) set({ status: next })
    else setT({ status: next })
  }

  const publishWarning = (() => {
    if (localeStatus !== "published") return null
    if (!tr) {
      return `${editLocale} has no translation yet, so publishing shows the ${store.defaultLocale} text to ${editLocale} readers.`
    }
    if (!tr.reviewed && editLocale !== store.defaultLocale) {
      return `${editLocale} has not been reviewed by a human but is published. Check the wording or set it back to draft.`
    }
    return null
  })()

  // Region and city choices are filtered by the selection above them, matching
  // the Directus form's progressive filtering.
  const regions = useMemo(
    () => (service.countryId ? db.regionsOfCountry(store, service.countryId) : []),
    [store, service.countryId],
  )
  const cities = useMemo(
    () => (service.regionId ? db.citiesOfRegion(store, service.regionId) : []),
    [store, service.regionId],
  )
  const providers = useMemo(
    () => (service.countryId ? db.providersOfCountry(store, service.countryId) : db.listEntities(store, "provider")),
    [store, service.countryId],
  )

  return (
    <>
      <Section title="Status & ownership" defaultOpen>
        <Field
          label={`Status — ${editLocale}`}
          hint={
            editLocale === store.defaultLocale
              ? "Other locales fall back to this unless they set their own."
              : hasOwnStatus
                ? "This locale is set independently of the source language."
                : `Following ${store.defaultLocale}. Changing this sets ${editLocale} on its own.`
          }
        >
          <Select value={localeStatus} onValueChange={v => setStatusForLocale(v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SERVICE_STATUSES.map(st => (
                <SelectItem key={st} value={st} className="text-sm">{st}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {publishWarning && (
          <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:border-amber-700/50 dark:bg-amber-950/40 dark:text-amber-300">
            {publishWarning}
          </p>
        )}
        <Field label="Provider">
          <EntityPicker
            store={store} locale={locale} kind="provider" options={providers} value={service.providerId}
            onChange={v => set({ providerId: v })}
            onOpen={v => onSelect("provider", v)}
            placeholder="No provider"
          />
        </Field>
      </Section>

      <Section title="Location" defaultOpen>
        <Field label="Country">
          <EntityPicker
            store={store} locale={locale} kind="country" options={db.listEntities(store, "country")} value={service.countryId}
            // Changing country invalidates the region and city beneath it.
            onChange={v => set({ countryId: v, regionId: undefined, cityId: undefined })}
            placeholder="No country"
          />
        </Field>
        <Field label="Region" hint={service.countryId ? undefined : "Pick a country first"}>
          <EntityPicker
            store={store} locale={locale} kind="region" options={regions} value={service.regionId}
            onChange={v => set({ regionId: v, cityId: undefined })}
            disabled={!service.countryId}
            placeholder="No region"
          />
        </Field>
        <Field label="City" hint={service.regionId ? undefined : "Pick a region first"}>
          <EntityPicker
            store={store} locale={locale} kind="city" options={cities} value={service.cityId}
            onChange={v => set({ cityId: v })}
            disabled={!service.regionId}
            placeholder="No city"
          />
        </Field>
        <ToggleField
          label="Has a physical location"
          checked={!!service.physicalLocation}
          onChange={v => set({ physicalLocation: v })}
        />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Latitude">
            <Input
              type="number" step="any" value={service.lat ?? ""}
              onChange={e => set({ lat: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="h-8 text-sm"
            />
          </Field>
          <Field label="Longitude">
            <Input
              type="number" step="any" value={service.lon ?? ""}
              onChange={e => set({ lon: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="h-8 text-sm"
            />
          </Field>
        </div>

        <ServiceMap
          store={store}
          locale={locale}
          // Only this service — sibling pins here are noise, and clicking one
          // would silently move the wrong record's location.
          services={[service]}
          selectedId={service.id}
          onPick={(lat, lon) => set({ lat, lon })}
          height={220}
        />
        <CoordinateReadout
          lat={service.lat}
          lon={service.lon}
          onClear={() => set({ lat: undefined, lon: undefined })}
        />
      </Section>

      <Section title="Categorization" defaultOpen>
        <Field label="Service categories">
          <MultiPicker
            store={store} locale={locale} kind="serviceCategory"
            value={service.categoryIds ?? []}
            onChange={ids => set({ categoryIds: ids })}
          />
        </Field>
        <Field label="Tags">
          <MultiPicker
            store={store} locale={locale} kind="tag"
            value={service.tagIds ?? []}
            onChange={ids => set({ tagIds: ids })}
          />
        </Field>
        <Field label="Populations served">
          <MultiPicker
            store={store} locale={locale} kind="population"
            value={service.populationIds ?? []}
            onChange={ids => set({ populationIds: ids })}
          />
        </Field>
        <Field label="Accessibility">
          <MultiPicker
            store={store} locale={locale} kind="accessibility"
            value={service.accessibilityIds ?? []}
            onChange={ids => set({ accessibilityIds: ids })}
          />
        </Field>
      </Section>

      <Section title="Public contact">
        <RowList
          rows={service.contactInfo ?? []}
          onChange={rows => set({ contactInfo: rows })}
          blank={{ channel: CONTACT_CHANNELS[0] as string, contactDetails: "" }}
          addLabel="Add contact channel"
          render={(row, update) => (
            <>
              <Select value={row.channel} onValueChange={v => update({ channel: v })}>
                <SelectTrigger className="h-8 w-[140px] shrink-0 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_CHANNELS.map(c => (
                    <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={row.contactDetails ?? ""}
                onChange={e => update({ contactDetails: e.target.value })}
                placeholder="Number, address or link"
                className="h-8 flex-1 text-sm"
              />
            </>
          )}
        />
      </Section>

      <Section title="Opening hours">
        <ToggleField
          label="Always open"
          hint="Directus hides the hours list when this is on."
          checked={!!service.alwaysOpen}
          onChange={v => set({ alwaysOpen: v })}
        />
        {!service.alwaysOpen && (
          <RowList
            rows={service.hours ?? []}
            onChange={rows => set({ hours: rows })}
            // Typed as the union, not the first literal, so any day is assignable.
            blank={{ day: HOUR_DAYS[0] as HourDay, open: "09:00", close: "17:00" }}
            addLabel="Add hours"
            render={(row, update) => (
              <>
                <Select value={row.day} onValueChange={v => update({ day: v as HourDay })}>
                  <SelectTrigger className="h-8 w-[150px] shrink-0 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HOUR_DAYS.map(d => (
                      // Directus stores "Monday to Friday " with a trailing
                      // space; keep the value, trim only the label.
                      <SelectItem key={d} value={d} className="text-xs">{d.trim()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="time" value={row.open ?? ""} onChange={e => update({ open: e.target.value })} className="h-8 w-[110px] text-sm" />
                <Input type="time" value={row.close ?? ""} onChange={e => update({ close: e.target.value })} className="h-8 w-[110px] text-sm" />
              </>
            )}
          />
        )}
      </Section>

      <Section title="Administrative contacts">
        <p className="mb-2 text-xs text-muted-foreground">Internal focal points — not shown to service users.</p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="First name"><Input value={service.contactName ?? ""} onChange={e => set({ contactName: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Last name"><Input value={service.contactLastName ?? ""} onChange={e => set({ contactLastName: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Title"><Input value={service.contactTitle ?? ""} onChange={e => set({ contactTitle: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Email"><Input value={service.contactEmail ?? ""} onChange={e => set({ contactEmail: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Phone"><Input value={service.contactPhone ?? ""} onChange={e => set({ contactPhone: e.target.value })} className="h-8 text-sm" /></Field>
        </div>
        <p className="mb-1 mt-3 text-xs font-medium text-muted-foreground">Secondary</p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="First name"><Input value={service.secondaryName ?? ""} onChange={e => set({ secondaryName: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Last name"><Input value={service.secondaryLastName ?? ""} onChange={e => set({ secondaryLastName: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Title"><Input value={service.secondaryTitle ?? ""} onChange={e => set({ secondaryTitle: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Email"><Input value={service.secondaryEmail ?? ""} onChange={e => set({ secondaryEmail: e.target.value })} className="h-8 text-sm" /></Field>
          <Field label="Phone"><Input value={service.secondaryPhone ?? ""} onChange={e => set({ secondaryPhone: e.target.value })} className="h-8 text-sm" /></Field>
        </div>
      </Section>

      <Section title="Media & provenance">
        <ImageField
          label="Header image"
          value={service.headerImage}
          onChange={v => set({ headerImage: v || undefined })}
        />
        <Field label="Source">
          <Input value={service.source ?? ""} onChange={e => set({ source: e.target.value })} placeholder="Manual" className="h-8 text-sm" />
        </Field>
        {service.oldid != null && (
          <p className="text-xs text-muted-foreground">Directus id: {service.oldid}</p>
        )}
      </Section>
    </>
  )
}

// ── Provider form ────────────────────────────────────────────────────────────

